import { useEffect, useId, useRef, useState, type FormEvent, type ReactElement } from 'react';
import type { Role, User } from '@chatapp-inbox/shared';
import { Button } from '../details/Button';
import { adminErrorMessage, userErrorField } from './error-message';
import { Field } from './Field';
import { RoleField } from './RoleField';
import { PASSWORD_RULE, validateNewUser, type Errors, type NewUserField } from './validation';
import { useCreateUser } from './use-users';
import styles from './forms.module.css';
import cardStyles from './UsersScreen.module.css';

interface NewUserFormProps {
  /** A pessoa desistiu: quem abriu o formulário devolve o foco ao botão "Novo usuário". */
  onCancel: () => void;
  /** O usuário foi criado: quem abriu fecha o formulário, avisa e leva o foco ao cartão novo. */
  onCreated: (user: User) => void;
}

/** Formulário de novo usuário: valida antes de enviar e mostra a regra da senha antes de errar. */
export function NewUserForm({ onCancel, onCreated }: NewUserFormProps): ReactElement {
  const headingId = useId();
  const create = useCreateUser();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('agent');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Errors<NewUserField>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  // O formulário acabou de abrir: o foco vai para o primeiro campo (o botão que o abriu saiu de cena).
  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const pending = create.isPending;

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (pending) return;
    const found = validateNewUser({ name, email, password });
    setErrors(found);
    setFormError(null);
    const refs = { name: nameRef, email: emailRef, password: passwordRef };
    const first = (['name', 'email', 'password'] as const).find((field) => found[field]);
    if (first) {
      refs[first].current?.focus();
      return;
    }
    create.mutate(
      { name: name.trim(), email: email.trim(), role, password },
      {
        onSuccess: (user) => {
          setPassword(''); // a senha não sobrevive ao sucesso (o formulário também sai de cena)
          onCreated(user);
        },
        onError: (error) => {
          const message = adminErrorMessage(
            error,
            'Não foi possível criar o usuário. Tente de novo.',
          );
          const field = userErrorField(error);
          if (field) {
            setErrors({ [field]: message });
            refs[field].current?.focus();
          } else {
            setErrors({});
            setFormError(message);
          }
        },
      },
    );
  };

  return (
    <section aria-labelledby={headingId} className={cardStyles.formCard}>
      <h3 id={headingId} className={cardStyles.formTitle}>
        Novo usuário
      </h3>
      <form className={styles.form} noValidate aria-busy={pending || undefined} onSubmit={submit}>
        <Field
          ref={nameRef}
          label="Nome"
          dir="auto"
          autoComplete="off"
          value={name}
          readOnly={pending}
          error={errors.name}
          onChange={(event) => setName(event.target.value)}
        />
        <Field
          ref={emailRef}
          label="E-mail"
          type="email"
          inputMode="email"
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          value={email}
          readOnly={pending}
          error={errors.email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <RoleField value={role} onChange={setRole} disabled={pending} />
        <Field
          ref={passwordRef}
          label="Senha"
          type="password"
          hint={PASSWORD_RULE}
          autoComplete="new-password"
          autoCapitalize="none"
          spellCheck={false}
          value={password}
          readOnly={pending}
          error={errors.password}
          onChange={(event) => setPassword(event.target.value)}
        />
        {formError && (
          <p role="alert" className={styles.alert}>
            {formError}
          </p>
        )}
        <div className={styles.actions}>
          <Button type="submit" variant="primary" busy={pending}>
            {pending ? 'Criando…' : 'Criar usuário'}
          </Button>
          <Button busy={pending} onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </section>
  );
}
